// components/asset-management/finance.ts
// Dependency-free financial primitives shared by the asset-management surfaces.
// Kept in its own module (no fast-formula-parser import) so the scenario engine —
// which is pulled in by components/dealerCommands.ts — can reuse the exact same
// procedure as the workbook engine without dragging the spreadsheet parser into
// that module graph.

/** IRR of a cash-flow series — Newton-Raphson with a bisection fallback. */
export function irr(cf: number[], guess = 0.1): number {
  if (cf.length < 2) return NaN;
  let r = Number.isFinite(guess) ? guess : 0.1;
  for (let i = 0; i < 100; i += 1) {
    let npvVal = 0;
    let deriv = 0;
    for (let t = 0; t < cf.length; t += 1) {
      const v = cf[t] ?? 0;
      npvVal += v / Math.pow(1 + r, t);
      deriv += (-t * v) / Math.pow(1 + r, t + 1);
    }
    if (Math.abs(deriv) < 1e-12) break;
    const next = r - npvVal / deriv;
    if (!Number.isFinite(next)) break;
    if (Math.abs(next - r) < 1e-7) return Math.round(next * 1e6) / 1e6;
    r = next;
  }
  const f = (rate: number): number => cf.reduce((s, v, t) => s + v / Math.pow(1 + rate, t), 0);
  let lo = -0.9;
  let hi = 5;
  let flo = f(lo);
  // No sign change across [-90%, +500%] ⇒ no real IRR in a sane range (e.g. =IRR pointed at a
  // range with no negative outflow). Return NaN (like Excel's #NUM!) rather than the un-converged
  // Newton iterate `r`, which can be an absurd value such as 7.4e12 that masquerades as a result.
  if (flo * f(hi) > 0) return NaN;
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    const fm = f(mid);
    if (Math.abs(fm) < 1e-6 || hi - lo < 1e-7) return Math.round(mid * 1e6) / 1e6;
    if (flo * fm < 0) hi = mid;
    else {
      lo = mid;
      flo = fm;
    }
  }
  return Math.round(((lo + hi) / 2) * 1e6) / 1e6;
}
